import { API } from '../../services'
import { Filters, Pagination } from '../../services/types'
import { HttpStatusCode } from 'axios'
import { defaultPagination, defaultSorting } from '../../services/defaults'
import { defineStore } from 'pinia'
import { setPagination } from '../../services/utils/utils'
import { useToast } from 'vuestic-ui'
import { TypeBusiness } from '../../types/Business'
import { isOkStatus, isSuccessStatus } from '../../utils/http'
import router from '../../router'
import { useUserStore } from '../user'
import { resolvePostAuthRoute, resolveRouteAfterBusinessSwitch } from '../../utils/contextSelection'

const { init } = useToast()
/** business Store */
export const useBusinessStore = defineStore('businessStore', {
  // Default Config State
  state: () => ({
    loading: false,
    filters: {} as Filters,
    pagination: defaultPagination,
    sorting: defaultSorting,
    userActiveBusinesses: [] as TypeBusiness[],
    businesses: [] as TypeBusiness[],
  }),
  // Actions
  actions: {
    initLoading() {
      this.loading = true
    },
    stopLoading() {
      this.loading = false
    },
    setActiveBusinesses(business: TypeBusiness[]) {
      this.userActiveBusinesses = business
    },
    setPagination(pagination: Pagination) {
      this.pagination = setPagination(pagination)
    },
    addBusiness(business: TypeBusiness) {
      this.businesses.unshift(business)
    },
    editBusiness(business: TypeBusiness) {
      const businessIndex = this.businesses.findIndex((business) => business.uuid === business?.uuid)
      this.businesses[businessIndex] = business
    },
    removeBusiness(businessUuid: string) {
      // Search and remove the business of the supplied id
      const index = this.businesses.findIndex((business) => business.uuid == businessUuid)
      // Return early if no business was found. Else, remove the business from the businesses list.
      if (index === -1) return
      this.businesses.splice(index, 1)
    },

    async dispatchGetUserBusiness(filters?: Filters): Promise<any> {
      this.initLoading()
      const { status, data } = await API.business.getUserBusinesses(filters, this.pagination).finally(this.stopLoading)

      return {
        success: isOkStatus(status),
        data: data.data,
      }
    },

    async dispatchSwitchActiveBusiness(businessUuid: string) {
      const userStore = useUserStore()

      this.initLoading()
      const { status, data } = await API.business.switchActiveBusiness(businessUuid).finally(this.stopLoading)

      if (isSuccessStatus(status)) {
        // Backend cleared session branch/location; drop stale labels only after success.
        userStore.clearActiveBranchAndLocationContext()
        init({ message: 'Business switched successfully', color: 'success' })

        const refreshed = await userStore.dispatchGetAuthenticatedUser({ force: true })
        if (!refreshed) {
          init({ message: 'Business switched, but session refresh failed. Please reload.', color: 'danger' })
          return
        }

        const destination = resolvePostAuthRoute(
          resolveRouteAfterBusinessSwitch(
            {
              requires_branch_selection: (data as any)?.requires_branch_selection === true,
              requires_inventory_location_selection: (data as any)?.requires_inventory_location_selection === true,
            },
            userStore.authUser,
          ),
          userStore.defaultDashboard,
          (slug) => userStore.hasModule(slug),
          (action, subject) => userStore.hasPermission(action, subject),
        )
        router.replace({ name: destination })
      }
    },

    async dispatchUnSetActiveBusiness() {
      const userStore = useUserStore()

      this.initLoading()
      const { status } = await API.business.unSetActiveBusiness().finally(this.stopLoading)

      if (isSuccessStatus(status)) {
        userStore.clearActiveBranchAndLocationContext()
        await userStore.dispatchGetAuthenticatedUser({ force: true })

        router.replace({ name: 'business-options-menu' })
      }
    },

    // Get Current User Businesses.
    async dispatchGetUserActiveBusiness(): Promise<any> {
      this.initLoading()
      const { status, data } = await API.business
        .getUserActiveBusinesses(this.filters, this.pagination)
        .finally(this.stopLoading)

      if (status === HttpStatusCode.Ok) {
        // set businesss
        this.setActiveBusinesses(data.data)
        this.setPagination({
          meta: data.meta,
        })
      }

      return {
        success: status == HttpStatusCode.Ok,
      }
    },
  },
})
